import { Vue, Component, Prop } from 'vue-property-decorator'
import type { AxiosError } from 'axios'
import uniqCid from '@/helpers/uniqCid'

import { SizeImage } from '@/types/image'
import SiriusAPI from '@/api'
import { checkHEIC } from '@/helpers/checkHEIC'

@Component({
  inheritAttrs: false,
})
export default class ImageUploader extends Vue {
  @Prop({ type: Boolean, default: false }) private readonly loading!: boolean
  @Prop({ type: Boolean, default: false }) private readonly rounded!: boolean
  @Prop({ type: Boolean, default: true })
  private readonly uploaderVisible!: boolean

  @Prop({ type: String, default: '' }) private readonly title!: string

  @Prop({
    type: Array,
    default: () => [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heic',
      'image/heif',
    ],
  })
  private readonly acceptFiles!: string[]

  @Prop({ type: String, default: '' }) private readonly infoText!: string
  @Prop({ type: Boolean, default: false })
  private readonly hiddenInfoText!: string
  @Prop({ type: String, default: 'content' })
  private readonly place!: string
  @Prop({ type: Boolean, default: false })
  private readonly strictDimmensionsChecking!: boolean
  @Prop({ type: String })
  private readonly invalidWidthMessage?: string
  @Prop({ type: String })
  private readonly invalidHeightMessage?: string
  @Prop({ type: Number, default: 0 }) private readonly minWidth!: number
  @Prop({ type: Number, default: 0 }) private readonly minHeight!: number
  @Prop({ type: Number, default: 15728640 }) private maxSize!: number
  @Prop({ type: Boolean, default: false }) private readonly error!: boolean
  @Prop({ type: Function, default: null })
  private readonly uploadFunction?: (payload: {
    file?: File
    image?: SizeImage
  }) => void
  @Prop({ type: Boolean, default: false })
  private readonly hideChangeButton!: boolean
  @Prop({ type: Boolean, default: false }) private readonly disabled!: boolean
  @Prop({ type: Number }) private readonly aspectRatio?: number
  @Prop({ type: Boolean, default: false })
  private readonly imageDinamicAspectRatio!: boolean
  @Prop({ type: Boolean, default: true }) private readonly multiple!: boolean

  private dragged = false
  private checking = false
  private pendingRequests: number[] = []
  private inputFileKey = Date.now()

  private get isShowSpinner() {
    return this.pendingRequests.length > 0 || this.checking || this.loading
  }

  private get iconSize() {
    if (this.place === 'new-expert-widget') {
      return 24
    }

    if (this.place === 'new-adv-expert-widget') {
      return this.$vuetify.breakpoint.lgAndUp ? 32 : 24
    }

    if (this.place === 'content') {
      return this.$vuetify.breakpoint.xsOnly ? 24 : 40
    }

    if (this.place === 'verstka-editor') {
      return this.$vuetify.breakpoint.xsOnly ? 24 : 40
    }

    if (this.place === 'popup-page') {
      return this.$vuetify.breakpoint.smAndDown ? 24 : 40
    }

    if (this.place === 'native-post') {
      return this.$vuetify.breakpoint.xsOnly ? 32 : 40
    }

    if (
      this.place === 'citation-widget' ||
      this.place === 'teaser-item-image' ||
      this.place === 'article-native-post' ||
      this.place === 'image-source' ||
      this.place === 'mailing-article-image' ||
      this.place === 'article-native-post' ||
      this.place === 'audio-message' ||
      this.place === 'company-page'
    ) {
      return 40
    }

    if (
      this.place === 'media-library' ||
      this.place === 'video-preview' ||
      this.place === 'gallery-widget'
    ) {
      return 30
    }

    return this.$vuetify.breakpoint.xsOnly ? 24 : 40
  }

  private get localInfoText() {
    if (this.hiddenInfoText) return ''

    if (this.infoText) return this.infoText

    const infoParts = [
      `Формат изображения — ${this.acceptFiles
        .map((value) => value.replace('image/', '').toUpperCase())
        .join(', ')}`,
    ]

    if (this.imageDinamicAspectRatio && this.minWidth && this.minHeight) {
      infoParts.push(
        `картинка может быть любой пропорции, но ширина изображения должна быть не менее ${this.minWidth}\xa0пикселей, а высота - не менее ${this.minHeight} пикселей`
      )
    } else if (this.minWidth && this.minHeight) {
      infoParts.push(
        `минимальные размеры\xa0—\xa0${this.minWidth}x${this.minHeight}`
      )
    }

    infoParts.push(
      `максимальный вес\xa0—\xa0${(this.maxSize / 1024 / 1024).toLocaleString(
        'ru-RU'
      )}\xa0Mb`
    )

    return `${infoParts.join('; ')}.`
  }

  private onDragover(e: DragEvent) {
    e.preventDefault()

    this.dragged = true
  }

  private onDragleave(e: DragEvent) {
    e.preventDefault()
    this.dragged = false
  }

  private async onDrop(e: DragEvent) {
    e.preventDefault()
    this.dragged = false
    const imageJSON = e.dataTransfer?.getData('siriusImage')
    const image = imageJSON && (JSON.parse(imageJSON) as SizeImage)

    if (image) {
      this.$emit('load:start')
      this.checking = true

      const checked = await this.checkImg(image)

      if (checked) {
        const sizeImage = await this.getSizeImage(image)

        if (this.uploadFunction) {
          this.uploadFunction({ image: sizeImage })
        } else {
          this.$emit('load', sizeImage)
        }
      }

      this.checking = false
    } else if (e.dataTransfer?.files.length) {
      this.setFiles(Array.from(e.dataTransfer.files))
    }
  }

  private onChangeFile(e: Event) {
    const inputFile = e.target as HTMLInputElement

    if (!inputFile.files) return

    this.setFiles(Array.from(inputFile.files))
  }

  private async setFiles(files: File[]) {
    this.inputFileKey++
    this.$emit('load:start')
    for (const file of files) {
      await this.setFile(file)
    }
  }

  private checkFile(file: File | null) {
    return new Promise<boolean>((resolve) => {
      if (!file) {
        this.$notify({
          type: 'error',
          title: 'Некорректный файл',
        })

        resolve(false)

        return
      }

      if (file.type && !this.acceptFiles.includes(file.type)) {
        this.$notify({
          type: 'error',
          title: 'Неподдерживаемый тип файла',
        })

        resolve(false)

        return
      }

      if (
        checkHEIC(file) &&
        !this.acceptFiles.some((type) =>
          ['image/heic', 'image/heif'].includes(type)
        )
      ) {
        this.$notify({
          type: 'error',
          title: 'Неподдерживаемый тип файла',
        })

        resolve(false)

        return
      }

      if (!file || file.size > this.maxSize) {
        this.$notify({
          type: 'error',
          title: 'Размер картинки больше максимального допустимого',
        })

        resolve(false)
      } else {
        resolve(true)
      }
    })
  }

  private checkImg(value: string | SizeImage) {
    return new Promise<boolean>((resolve) => {
      if (!this.minWidth && !this.minHeight) {
        resolve(true)

        return
      }

      const check = ({ width, height }: { width: number; height: number }) => {
        const isValidWidth = width >= this.minWidth
        const isValidHeight = height >= this.minHeight
        const isValidDimmensions = isValidWidth && isValidHeight

        if (!isValidWidth) {
          this.$notify({
            type: 'error',
            title:
              this.invalidWidthMessage ||
              `Минимальная ширина изображения должна быть не менее ${this.minWidth}px`,
          })
        } else if (!isValidHeight) {
          this.$notify({
            type: 'error',
            title:
              this.invalidHeightMessage ||
              `Минимальная высота изображения должна быть не менее ${this.minHeight}px`,
          })
        }

        if (this.strictDimmensionsChecking) {
          resolve(isValidDimmensions)
        } else {
          resolve(true)
        }
      }

      if (typeof value === 'string') {
        const img = new Image()

        img.src = value

        img.onload = () => {
          check({
            width: img.width,
            height: img.height,
          })
        }

        img.onerror = () => {
          resolve(false)
        }
      } else {
        check({
          width: value.width,
          height: value.width,
        })
      }
    })
  }

  private async addImage(file: File) {
    if (!file) return

    const requestId = uniqCid()

    this.pendingRequests.push(requestId)

    const fileName = file.name

    try {
      if (this.uploadFunction) {
        await this.uploadFunction({
          file,
        })
      } else {
        const data = new FormData()

        data.append('file', file)

        const { item } = await SiriusAPI.image.addImage({
          file: data,
          isShowNotify: false,
        })

        const sizeImage = await this.getSizeImage({
          ...item,
          file,
        })

        this.$emit('load', sizeImage)
      }
    } catch (e) {
      const error = e as AxiosError<string>

      const errorMessage =
        error.response?.data && typeof error.response?.data === 'string'
          ? error.response.data
          : `Произошла ошибка при загрузке изображения ${fileName}`

      this.$notify({
        type: 'error',
        title: errorMessage,
      })

      this.$emit('error')
    } finally {
      this.pendingRequests = this.pendingRequests.filter(
        (id) => id !== requestId
      )
    }
  }

  private getSizeImage(
    value: Omit<SizeImage, 'height' | 'width'> &
      Partial<
        Pick<SizeImage, 'height' | 'width'> & {
          file: File
        }
      >
  ) {
    return new Promise<SizeImage>((resolve) => {
      if (value.width && value.height) {
        resolve(value as SizeImage)

        return
      }

      const img = new Image()

      img.src = value.url

      img.onload = () => {
        resolve({
          ...value,
          width: img.width,
          height: img.height,
        })
      }
    })
  }

  private async setFile(file: File | null) {
    if (!file) return

    this.checking = true

    try {
      const checkedFile = await this.checkFile(file)

      let checkedImg = true

      if (checkedFile && !checkHEIC(file)) {
        const objectURL = URL.createObjectURL(file)

        checkedImg = await this.checkImg(objectURL)

        URL.revokeObjectURL(objectURL)
      }

      const checked = checkedFile && checkedImg

      if (checked) {
        await this.addImage(file)
      } else {
        this.$emit('error')
      }
    } finally {
      this.checking = false
    }
  }
}
